import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  TextField,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  FilterList as FilterListIcon,
  TrendingUp as TrendingUpIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Psychology as PsychologyIcon,
  Speed as SpeedIcon,
  Add as AddIcon
} from '@mui/icons-material';

const ChatHistoryPage = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);

  // 필터링 및 페이지네이션 상태
  const [filters, setFilters] = useState({
    search: '',
    response_type: '',
    start_date: null,
    end_date: null
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    total_pages: 0
  });

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    loadChatHistory();
    loadStats();
  }, [pagination.page, pagination.limit, filters]);

  const loadChatHistory = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString()
      });

      if (filters.search) params.append('search', filters.search);
      if (filters.response_type) params.append('response_type', filters.response_type);
      if (filters.start_date) params.append('start_date', filters.start_date.toISOString());
      if (filters.end_date) params.append('end_date', filters.end_date.toISOString());

      const response = await fetch(`/api/chat-history/list?${params}`);
      
      if (!response.ok) {
        throw new Error(`서버 오류: ${response.status}`);
      }

      const data = await response.json();
      setRecords(data.records);
      setPagination(prev => ({
        ...prev,
        total: data.total,
        total_pages: data.total_pages
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch('/api/chat-history/stats');
      
      if (!response.ok) {
        throw new Error(`통계 로드 실패: ${response.status}`);
      }

      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('통계 로드 오류:', err);
    }
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    loadChatHistory();
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleViewDetail = (record) => {
    setSelectedRecord(record);
    setDetailDialogOpen(true);
  };

  const handleDeleteClick = (record) => {
    setRecordToDelete(record);
    setDeleteDialogOpen(true);
  };

  const handleAddToCurated = (record) => {
    // 큐레이션된 답변 페이지로 이동하면서 데이터 전달
    navigate('/admin/faq', {
      state: {
        fromChatHistory: true,
        chatData: {
          question: record.original_question || record.user_question,
          answer: record.assistant_answer,
          category: record.selected_product || '',
          keywords: ''
        }
      }
    });
  };

  const handleDeleteConfirm = async () => {
    if (!recordToDelete) return;

    try {
      const response = await fetch(`/api/chat-history/${recordToDelete.id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error(`삭제 실패: ${response.status}`);
      }

      setDeleteDialogOpen(false);
      setRecordToDelete(null);
      loadChatHistory();
      loadStats();
    } catch (err) {
      setError(err.message);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    // UTC 시간을 한국 시간(UTC+9)으로 변환
    const koreanTime = new Date(date.getTime() + (9 * 60 * 60 * 1000));
    return koreanTime.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit', 
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const getResponseTypeChip = (type) => {
    if (type === 'curated') {
      return <Chip label="큐레이션된 답변" color="primary" size="small" />;
    } else {
      return <Chip label="RAG 시스템" color="secondary" size="small" />;
    }
  };

  const truncateText = (text, maxLength = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        질의응답 내역
      </Typography>

      {/* 통계 카드 */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <QuestionAnswerIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6">{stats.total_conversations}</Typography>
                <Typography variant="body2" color="text.secondary">
                  전체 대화 수
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <TrendingUpIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                <Typography variant="h6">{stats.curated_responses}</Typography>
                <Typography variant="body2" color="text.secondary">
                  큐레이션된 답변
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <PsychologyIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                <Typography variant="h6">{stats.rag_responses}</Typography>
                <Typography variant="body2" color="text.secondary">
                  RAG 시스템 답변
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <SpeedIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                <Typography variant="h6">
                  {stats.avg_processing_time_ms ? `${stats.avg_processing_time_ms.toFixed(0)}ms` : 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  평균 처리 시간
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* 필터 섹션 */}
      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <FilterListIcon sx={{ mr: 1 }} />
          <Typography>필터 및 검색</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="검색어"
                placeholder="질문 또는 답변에서 검색"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={handleSearch}>
                      <SearchIcon />
                    </IconButton>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>응답 타입</InputLabel>
                <Select
                  value={filters.response_type}
                  label="응답 타입"
                  onChange={(e) => handleFilterChange('response_type', e.target.value)}
                >
                  <MenuItem value="">전체</MenuItem>
                  <MenuItem value="curated">큐레이션된 답변</MenuItem>
                  <MenuItem value="rag">RAG 시스템</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="시작 날짜"
                type="date"
                value={filters.start_date ? filters.start_date.toISOString().split('T')[0] : ''}
                onChange={(e) => handleFilterChange('start_date', e.target.value ? new Date(e.target.value) : null)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="종료 날짜"
                type="date"
                value={filters.end_date ? filters.end_date.toISOString().split('T')[0] : ''}
                onChange={(e) => handleFilterChange('end_date', e.target.value ? new Date(e.target.value) : null)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* 데이터 테이블 */}
      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>번호</TableCell>
                      <TableCell>선택 제품</TableCell>
                      <TableCell>원본 질문</TableCell>
                      <TableCell>처리된 질문</TableCell>
                      <TableCell>답변</TableCell>
                      <TableCell>응답 타입</TableCell>
                      <TableCell>처리 시간</TableCell>
                      <TableCell>생성 시간</TableCell>
                      <TableCell>작업</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {records.map((record, index) => (
                      <TableRow key={record.id}>
                        <TableCell>{pagination.total - ((pagination.page - 1) * pagination.limit + index)}</TableCell>
                        <TableCell>
                          {record.selected_product ? (
                            <Chip 
                              label={record.selected_product} 
                              color="info" 
                              size="small" 
                              variant="outlined"
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={record.original_question || record.user_question}>
                            <Typography variant="body2">
                              {truncateText(record.original_question || record.user_question, 40)}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={record.user_question}>
                            <Typography variant="body2">
                              {truncateText(record.user_question, 40)}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={record.assistant_answer}>
                            <Typography variant="body2">
                              {truncateText(record.assistant_answer, 40)}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          {getResponseTypeChip(record.response_type)}
                        </TableCell>
                        <TableCell>
                          {record.processing_time_ms ? `${record.processing_time_ms}ms` : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {formatDate(record.created_at)}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetail(record)}
                            color="primary"
                          >
                            <VisibilityIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(record)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* 페이지네이션 */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={pagination.total_pages}
                  page={pagination.page}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* 상세 보기 다이얼로그 */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>질의응답 상세 내역</DialogTitle>
        <DialogContent>
          {selectedRecord && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="h6" gutterBottom>
                선택된 제품
              </Typography>
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography>
                  {selectedRecord.selected_product ? (
                    <Chip 
                      label={selectedRecord.selected_product} 
                      color="info" 
                      size="medium" 
                      variant="outlined"
                    />
                  ) : (
                    '선택된 제품 없음'
                  )}
                </Typography>
              </Paper>

              <Typography variant="h6" gutterBottom>
                사용자 원본 질문
              </Typography>
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography>{selectedRecord.original_question || selectedRecord.user_question}</Typography>
              </Paper>

              <Typography variant="h6" gutterBottom>
                처리된 질문 (LLM 전달)
              </Typography>
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography>{selectedRecord.user_question}</Typography>
              </Paper>

              <Typography variant="h6" gutterBottom>
                어시스턴트 답변
              </Typography>
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography>{selectedRecord.assistant_answer}</Typography>
              </Paper>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">응답 타입</Typography>
                  <Typography>{getResponseTypeChip(selectedRecord.response_type)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">처리 시간</Typography>
                  <Typography>
                    {selectedRecord.processing_time_ms ? `${selectedRecord.processing_time_ms}ms` : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">세션 ID</Typography>
                  <Typography>{selectedRecord.session_id || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">생성 시간</Typography>
                  <Typography>{formatDate(selectedRecord.created_at)}</Typography>
                </Grid>
              </Grid>

              {selectedRecord.source_documents && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    참고 문서
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>
                      {JSON.stringify(JSON.parse(selectedRecord.source_documents), null, 2)}
                    </pre>
                  </Paper>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>
            닫기
          </Button>
          <Button 
            onClick={() => handleAddToCurated(selectedRecord)}
            variant="contained"
            startIcon={<AddIcon />}
            color="success"
          >
            큐레이션된 답변으로 추가
          </Button>
        </DialogActions>
      </Dialog>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>질의응답 내역 삭제</DialogTitle>
        <DialogContent>
          <Typography>
            이 질의응답 내역을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            취소
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            삭제
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatHistoryPage; 